import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowLeft, FaPaperPlane, FaBook, FaCheckCircle, FaEnvelope, FaUniversity, FaGraduationCap, FaCalendarAlt, FaLightbulb } from 'react-icons/fa';
import { supabase } from '../lib/supabase';
import { useLanguage } from '../contexts/LanguageContext';
import usePageTitle from '../hooks/usePageTitle';

const PkmBank = () => {
  usePageTitle('PKM Bank');
  const { t } = useLanguage();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [kampus, setKampus] = useState('');
  const [jurusan, setJurusan] = useState('');
  const [angkatan, setAngkatan] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');

    if (!name.trim() || !email.trim() || !kampus.trim() || !jurusan.trim() || !angkatan.trim() || !reason.trim()) {
      setErrorMessage(t('pkmBank.allFieldsRequired'));
      setLoading(false);
      return;
    }

    try {
      const { error } = await supabase.from('pkm_bank_requests').insert([{
        name: name.trim(),
        email: email.trim(),
        kampus: kampus.trim(),
        jurusan: jurusan.trim(),
        angkatan: angkatan.trim(),
        reason: reason.trim(),
      }]);

      if (error) throw error;
      setSuccess(true);
    } catch (err) {
      setErrorMessage(t('pkmBank.submitError') + ': ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-dark-900 flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center bg-white dark:bg-dark-800 rounded-2xl shadow-xl p-8 border border-gray-100 dark:border-dark-700">
          <div className="w-20 h-20 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-6">
            <FaCheckCircle className="text-4xl text-green-500" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">{t('pkmBank.successTitle')}</h2>
          <p className="text-gray-500 dark:text-gray-400 mb-2">{t('pkmBank.successDesc')}</p>
          <p className="text-sm text-gray-400 dark:text-gray-500 mb-8">{t('pkmBank.successDesc2')}</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3 px-6 rounded-xl transition-colors"
          >
            <FaArrowLeft className="text-sm" /> {t('pkmBank.backToHome')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-900 py-12">
      <div className="max-w-2xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium mb-4 transition-colors"
          >
            <FaArrowLeft className="text-xs" /> {t('pkmBank.backToHome')}
          </Link>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg">
              <FaBook className="text-white text-xl" />
            </div>
            <div>
              <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white">{t('pkmBank.title')}</h1>
              <p className="text-gray-500 dark:text-gray-400 text-sm">{t('pkmBank.subtitle')}</p>
            </div>
          </div>
        </div>

        {/* Info Box */}
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border border-amber-200 dark:border-amber-700/40 rounded-xl p-5 mb-8">
          <div className="flex gap-3">
            <FaLightbulb className="text-amber-500 text-lg mt-0.5 shrink-0" />
            <div>
              <p className="text-sm text-amber-800 dark:text-amber-300 font-semibold mb-1">{t('pkmBank.infoTitle')}</p>
              <p className="text-sm text-amber-700 dark:text-amber-400/80">{t('pkmBank.infoDesc')}</p>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white dark:bg-dark-800 rounded-2xl shadow-lg border border-gray-100 dark:border-dark-700 p-6 sm:p-8 space-y-6">
          {errorMessage && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700/40 text-red-700 dark:text-red-400 px-4 py-3 rounded-xl text-sm">
              {errorMessage}
            </div>
          )}

          {/* Name */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
              {t('pkmBank.name')} <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full border border-gray-300 dark:border-dark-600 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-dark-700 dark:text-white transition-colors"
              placeholder={t('pkmBank.namePlaceholder')}
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
              <FaEnvelope className="inline mr-1.5 text-xs opacity-50" />
              {t('pkmBank.email')} <span className="text-red-400">*</span>
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full border border-gray-300 dark:border-dark-600 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-dark-700 dark:text-white transition-colors"
              placeholder={t('pkmBank.emailPlaceholder')}
            />
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{t('pkmBank.emailHint')}</p>
          </div>

          {/* Kampus & Jurusan */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                <FaUniversity className="inline mr-1.5 text-xs opacity-50" />
                {t('pkmBank.campus')} <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={kampus}
                onChange={(e) => setKampus(e.target.value)}
                className="w-full border border-gray-300 dark:border-dark-600 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-dark-700 dark:text-white transition-colors"
                placeholder={t('pkmBank.campusPlaceholder')}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                <FaGraduationCap className="inline mr-1.5 text-xs opacity-50" />
                {t('pkmBank.major')} <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={jurusan}
                onChange={(e) => setJurusan(e.target.value)}
                className="w-full border border-gray-300 dark:border-dark-600 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-dark-700 dark:text-white transition-colors"
                placeholder={t('pkmBank.majorPlaceholder')}
              />
            </div>
          </div>

          {/* Angkatan */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
              <FaCalendarAlt className="inline mr-1.5 text-xs opacity-50" />
              {t('pkmBank.batch')} <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={angkatan}
              onChange={(e) => setAngkatan(e.target.value)}
              className="w-full border border-gray-300 dark:border-dark-600 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-dark-700 dark:text-white transition-colors"
              placeholder={t('pkmBank.batchPlaceholder')}
            />
          </div>

          {/* Reason */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
              {t('pkmBank.reason')} <span className="text-red-400">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              className="w-full border border-gray-300 dark:border-dark-600 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-dark-700 dark:text-white transition-colors resize-none"
              placeholder={t('pkmBank.reasonPlaceholder')}
            />
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold py-3.5 px-6 rounded-xl shadow-lg shadow-amber-500/25 hover:shadow-xl hover:shadow-amber-500/30 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                {t('pkmBank.submitting')}
              </>
            ) : (
              <>
                <FaPaperPlane className="text-sm" /> {t('pkmBank.submit')}
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default PkmBank;
